import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import RecordFormDialog from "@/components/RecordFormDialog";
import { levelLabel } from "@/lib/levels";
import { fmtDateOnly } from "@/lib/datetime";
import {
  ArrowLeft,
  Loader2,
  Award,
  BookMarked,
  Repeat,
  StickyNote,
  User,
  Plus,
  Pencil,
  HeartPulse,
  CalendarClock,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-xs uppercase tracking-wide font-semibold">{label}</span>
      </div>
      <p className="mt-2 font-heading text-2xl font-medium text-foreground">{value}</p>
    </div>
  );
}

function TextLine({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-3">
      <Icon className="h-4 w-4 mt-1 text-primary shrink-0" />
      <p className="text-sm min-w-0">
        <span className="font-medium text-foreground">{label}: </span>
        <span className="text-muted-foreground whitespace-pre-wrap">{value}</span>
      </p>
    </div>
  );
}

export default function StudentProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/teacher/students/${id}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load student");
      toast.error(e.response?.data?.detail || "Failed to load student");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };
  const openEdit = (rec) => {
    setEditingRecord(rec);
    setFormOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center mt-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl">
        <Link to="/teacher" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to My Students
        </Link>
        <div className="mt-6 bg-card border border-border rounded-lg p-10 text-center text-muted-foreground" data-testid="profile-error">
          {error}
        </div>
      </div>
    );
  }

  const { student, records, summary } = data;
  const fmtDate = fmtDateOnly;

  return (
    <div className="max-w-3xl" data-testid="student-profile-page">
      <Link
        to="/teacher"
        data-testid="profile-back-link"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to My Students
      </Link>

      {/* Header */}
      <div className="mt-4 bg-card border border-border rounded-lg p-6">
        <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground" data-testid="profile-student-name">
          {student.full_name}
        </h1>
        <div className="mt-3 text-sm">
          <span className="text-muted-foreground">Level</span>
          <span className="ml-2 text-foreground font-medium" data-testid="profile-level">{levelLabel(student.level) || "—"}</span>
        </div>
        {student.session && (
          <div className="mt-1.5 flex items-center gap-2 text-sm" data-testid="profile-session">
            <CalendarClock className="h-4 w-4 text-primary" />
            <span className="text-muted-foreground">Session</span>
            <span className="text-foreground font-medium">{student.session}</span>
          </div>
        )}
        {student.assigned_teachers?.length > 1 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Shared with:</span>
            {student.assigned_teachers.map((t) => (
              <Badge key={t.id} variant="secondary" className="font-normal gap-1">
                <User className="h-3 w-3" /> {t.full_name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Medical callout — shown only when notes exist */}
      {student.medical_notes && (
        <div
          className="mt-4 flex items-start gap-3 rounded-lg border-l-4 border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 px-4 py-3"
          data-testid="profile-medical-callout"
        >
          <HeartPulse className="h-5 w-5 mt-0.5 text-[hsl(var(--gold))] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Medical / learning notes</p>
            <p className="mt-0.5 text-sm text-muted-foreground whitespace-pre-wrap" data-testid="profile-medical-notes">
              {student.medical_notes}
            </p>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="mt-4 max-w-[220px]">
        <StatCard icon={Award} label="Points" value={summary.total_points} testid="stat-points" />
      </div>

      {/* History */}
      <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-medium text-foreground">Daily record history</h2>
          <p className="text-sm text-muted-foreground">{summary.total_records} records · most recent first</p>
        </div>
        <Button onClick={openCreate} className="h-11" data-testid="add-record-button">
          <Plus className="h-4 w-4 mr-2" /> Add / edit record
        </Button>
      </div>

      {records.length === 0 ? (
        <div className="mt-4 bg-card border border-border rounded-lg p-10 text-center text-muted-foreground" data-testid="profile-no-records">
          No daily records yet.
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="profile-records-list">
          {records.map((r) => (
            <div key={r.id} className="bg-card border border-border rounded-lg p-5" data-testid={`record-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="font-medium text-foreground">{fmtDate(r.date)}</p>
                <Badge variant="secondary" className="font-normal gap-1">
                  <Award className="h-3 w-3" /> {r.points} pts
                </Badge>
              </div>

              <div className="mt-3 space-y-2.5">
                <TextLine icon={BookMarked} label="Memorization" value={r.memorization} />
                <TextLine icon={Repeat} label="Review" value={r.review} />
                <TextLine icon={StickyNote} label="Notes" value={r.other_notes} />
                {!r.memorization && !r.review && !r.other_notes && (
                  <p className="text-sm text-muted-foreground">No progress notes for this day.</p>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Recorded by {r.recorded_by}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEdit(r)}
                  data-testid={`edit-record-${r.id}`}
                  className="h-8"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RecordFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        studentId={id}
        record={editingRecord}
        onSaved={load}
      />
    </div>
  );
}
